import { useCallback, useEffect, useRef, useState } from "react";

/**
 * One way to load (owner, 15 Oct 2026 - docs/load-failure-enumeration.md §7a). Every screen used to decide for itself
 * what to show when a load failed, so the default was whatever its author happened to write - and 128 times it was the
 * empty state. A load here is in one of four states, and the screen is told which:
 *
 *   idle      - not yet asked (`enabled` false, or nothing to ask yet)
 *   loading   - asked, no answer yet
 *   failed    - the server refused, has no such thing, or the request failed: `problem` (REFUSED | NOT_FOUND | FAILED),
 *               `error`, and `request`, the path that failed - the refusing-server guard matches it to what is shown
 *   succeeded - `data`, which may be empty
 *
 * Render it with <Loaded>: an empty state is reachable only from "succeeded and returned nothing".
 *
 * `request` returns a promise of the data itself (`() => get("/x")`, or `() => axios.get(url).then((r) => r.data)`).
 * A newer load supersedes an older one: an answer to a request no longer current is dropped, never shown.
 */
export function useLoad(request, deps, { enabled = true } = {}) {
  const [state, setState] = useState(() => ({ status: enabled ? "loading" : "idle" }));
  const generation = useRef(0);
  const requestRef = useRef(request);
  requestRef.current = request;

  const run = useCallback(() => {
    const mine = ++generation.current;
    setState((previous) => ({ status: "loading", data: previous.data }));
    let promise;
    try {
      promise = Promise.resolve(requestRef.current());
    } catch (e) {
      promise = Promise.reject(e);
    }
    promise.then(
      (data) => { if (generation.current === mine) setState({ status: "succeeded", data }); },
      (error) => {
        if (generation.current === mine) {
          setState({ status: "failed", error, problem: loadProblem(error), request: requestPath(error) });
        }
      });
  }, []);

  useEffect(() => {
    if (!enabled) {
      generation.current += 1;
      setState({ status: "idle" });
      return undefined;
    }
    run();
    return () => { generation.current += 1; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, ...deps]);

  return { ...state, reload: run };
}

/** What went wrong: the server refused (403), there is no such thing (404), or it failed - anything else. */
export function loadProblem(error) {
  const status = error?.response?.status;
  return status === 403 ? "REFUSED" : status === 404 ? "NOT_FOUND" : "FAILED";
}

/** The server's own sentence about a failed load, when it gave one. */
export function loadProblemMessage(error) {
  const message = error?.response?.data?.message;
  return typeof message === "string" && message.trim() ? message.trim() : null;
}

/** The path of the request that failed (no host, no query) - what the refusing-server guard matches against. */
export function requestPath(error) {
  const url = error?.config?.url;
  if (!url) return null;
  try {
    return new URL(url, window.location.origin).pathname;
  } catch (e) {
    return null;
  }
}
